import {Color} from '@benchling/nucleus-ui';
import {createStyles, Text, useMantineTheme} from '@mantine/core';
import React from 'react';
import {NodeProps} from 'reactflow';
import {Node} from 'reactflow';

import getTestProps from 'test/lib/cypress/getTestProps';
import {BenchlingNode} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNode';
import {NODE_HOVER_BOX_SHADOW} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNodeDesignConstants';
import {COMMON_NODE_WIDTH} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNodeDesignConstants';

export const PROCEDURE_RUN_DESIGN_FLOWCHART_NODE_TYPE = 'procedureRunDesignFlowchartNode';

/** Matches COMMON_NODE_WIDTH used by BenchlingNode, which is set as the node's rendered width. */
export const FALLBACK_NODE_WIDTH = COMMON_NODE_WIDTH;
/** Estimated height of a node before ReactFlow measures it. Approximates a node with no description. */
export const FALLBACK_NODE_HEIGHT = 50;

export interface ProcedureRunDesignFlowchartNodeData {
  conditionName: string;
  replicateNumber: number;
  conditionDescription?: string;
  methodId: string;
  isHovered: boolean;
  isHighlighted: boolean;
}

export function createProcedureRunDesignFlowchartNode({
  conditionIndex,
  replicateIndex,
  methodId,
  conditionName,
  replicateNumber,
  conditionDescription,
  isHovered,
  isHighlighted,
}: {
  conditionIndex: number;
  replicateIndex: number;
  methodId: string;
  conditionName: string;
  replicateNumber: number;
  conditionDescription?: string;
  isHovered: boolean;
  isHighlighted: boolean;
}): Node<ProcedureRunDesignFlowchartNodeData> {
  return {
    id: `${conditionIndex}-${replicateIndex}-${methodId}`,
    type: PROCEDURE_RUN_DESIGN_FLOWCHART_NODE_TYPE,
    data: {
      conditionName,
      replicateNumber,
      conditionDescription,
      methodId,
      isHovered,
      isHighlighted,
    },
    position: {
      x: 0,
      y: 0,
    },
    selectable: false,
    deletable: false,
    draggable: false,
    connectable: false,
  };
}

export function isProcedureRunDesignFlowchartNode(
  node: Node
): node is Node<ProcedureRunDesignFlowchartNodeData> {
  return node.type === PROCEDURE_RUN_DESIGN_FLOWCHART_NODE_TYPE;
}

export default function ProcedureRunDesignFlowchartNode(
  props: NodeProps<ProcedureRunDesignFlowchartNodeData>
): JSX.Element {
  const {classes} = useStyles();
  const theme = useMantineTheme();
  const {data} = props;
  const {isHovered, isHighlighted} = data;
  const headerBg = isHovered || isHighlighted ? theme.colors.blue[0] : theme.white;
  return (
    <BenchlingNode
      isSelectable={true}
      isSelected={isHighlighted}
      w="200px"
      sx={{
        backgroundColor: headerBg,
        ...(isHovered && {boxShadow: NODE_HOVER_BOX_SHADOW}),
      }}
      dataTestAttrs={{
        element: 'root',
        key: props.id,
      }}
    >
      <BenchlingNode.Header
        bg={headerBg}
        {...getTestProps({component: 'ProcedureRunDesignFlowchartNode', element: 'header'})}
      >
        <Text className={classes.conditionReplicate}>
          {`${data.conditionName} - ${data.replicateNumber}`}
        </Text>
        {data.conditionDescription ? (
          <Text className={classes.description}>{data.conditionDescription}</Text>
        ) : null}
      </BenchlingNode.Header>
      {/* TODO (BNCH-153699): Include sections with varied parameters */}
    </BenchlingNode>
  );
}

const useStyles = createStyles((theme) => ({
  conditionReplicate: {
    fontWeight: 600,
    fontSize: theme.fontSizes.sm,
    lineHeight: '16px',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  description: {
    color: Color.GRAY_600,
    fontSize: theme.fontSizes.xxs,
    lineHeight: '14px',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
}));
